/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Record matching", description = "Learn how Hightouch matches records from your model rows to your destination records.", lastUpdatedDate = new Date(1681689600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Record matching",
        description: "Learn how Hightouch matches records from your model rows to your destination records.",
        lastUpdatedDate: "Mon Apr 17 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Destination field selection",
          "depth": 0,
          "id": "destination-field-selection"
        }, {
          "value": "External IDs",
          "depth": 1,
          "id": "external-ids"
        }, {
          "value": "Model column selection",
          "depth": 0,
          "id": "model-column-selection"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      img: "img",
      strong: "strong",
      h2: "h2",
      code: "code",
      h3: "h3",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.p, {
        children: ["If you're using ", _jsx(_components.a, {
          href: "/syncs/types-and-modes",
          children: "update, upsert, or archive mode"
        }), " to sync data, one of the first steps of your sync configuration is defining how Hightouch should match rows from your model to existing destination records."]
      }), "\n", _jsx(_components.p, {
        children: "To do this, Hightouch asks you to select the model column and destination field to match on."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "working-with-data/record-matching.png",
          alt: "Record matching in Hightouch UI"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "\"Columns\" vs. \"fields\" vs. \"properties\""
          }), ": Though some may use these terms\ninterchangably,\nHightouch uses the term \"columns\" to refer to a set of vertical data values in\na source or model and \"fields\" or \"properties\" to refer to them in a destination."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "destination-field-selection",
        children: "Destination field selection"
      }), "\n", _jsx(_components.p, {
        children: "It's often best to start by selecting the destination field to match on because some destinations have requirements or recommendations on the field to use."
      }), "\n", _jsx(_components.p, {
        children: "For example, some destinations require matching on PII fields like email or phone number. Hightouch only displays permitted fields in the dropdown when selecting a destination field to match records on."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, ", _jsx(_components.a, {
          href: "/destinations/attentive#matching-users",
          children: "Attentive"
        }), " allows you to match users on several fields, including email, phone number, and various IDs."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "working-with-data/record-matching-destination-fields.png",
          alt: "Record matching in Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If there is a recommended field, Hightouch displays ", _jsx(_components.strong, {
          children: "(recommended)"
        }), " next to the field name. For example, ", _jsx(_components.a, {
          href: "/destinations/salesforce",
          children: "Salesforce"
        }), " recommends using an external ID field like ", _jsx(_components.code, {
          children: "External_ID__c"
        }), " for matching."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "working-with-data/record-matching-recommended-fields.png",
          alt: "Record matching in Hightouch UI"
        })
      }), "\n", _jsx(_components.p, {
        children: "Refer to the record matching section of each destination doc for more information."
      }), "\n", _jsx(_components.h3, {
        id: "external-ids",
        children: "External IDs"
      }), "\n", _jsxs(_components.p, {
        children: ["Some destinations, for example, ", _jsx(_components.a, {
          href: "https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/external-id/",
          children: "Facebook Conversions API"
        }), " and ", _jsx(_components.a, {
          href: "/destinations/salesforce",
          children: "Salesforce"
        }), ", use the concept of ", _jsx(_components.strong, {
          children: "external IDs"
        }), ". External IDs refer to unique identifiers from another system \"external\" to the destination platform. This allows the platform to maintain its own primary unique identifiers while enabling associations with data from other platforms."]
      }), "\n", _jsxs(_components.p, {
        children: ["Each platform may have different specifications for fields used as external IDs. For example, ", _jsx(_components.a, {
          href: "https://help.salesforce.com/s/articleView?id=000385174&type=1",
          children: "Salesforce"
        }), " requires ", _jsx(_components.code, {
          children: "External ID"
        }), " type fields to be the explicit External ID type in Salesforce, not just an external ID field by practice. Ensure that any destination field you select for record matching complies to the platform's specifications."]
      }), "\n", _jsx(_components.h2, {
        id: "model-column-selection",
        children: "Model column selection"
      }), "\n", _jsx(_components.p, {
        children: "When selecting which model column to use, you need to consider more than the destination requirements."
      }), "\n", _jsxs(_components.p, {
        children: ["Since the column you select is used to identify which records to update, it's imperative to ", _jsx(_components.strong, {
          children: "choose a column with unique values"
        }), ". For that reason, the model column you choose is often the same as the ", _jsx(_components.a, {
          href: "/getting-started/concepts#unique-primary-key-requirement",
          children: "primary key"
        }), " column you select for your model. Though the model's primary key can be used for record matching, this isn't mandatory."]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, you may have ", _jsx(_components.code, {
          children: "email"
        }), " and ", _jsx(_components.code, {
          children: "user_id"
        }), " fields in your model. Which you choose for record matching depends on:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Which fields in your destination are available and expected for matching"
        }), "\n", _jsx(_components.li, {
          children: "Which field is guaranteed to be unique"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If both fields are accepted by the destination for matching, you should choose the one which you are more confident is strictly unique."
      })]
    });
  }
}
export default MDXContent;
